import { generateId } from '@freelancer/datastore/testing/helpers';
import { v4 as uuidv4 } from 'uuid';
import type {
  SwiftIdApplication,
  SwiftIdApplicationData,
} from './swift-id-application.model';

/**
 * Options for generating a SwiftID application
 */
export interface GenerateSwiftIdApplicationOptions {
  readonly id?: number;
  readonly uuid?: string;
  readonly advisorCode?: string;
  readonly brokerageCode?: string;
  readonly branchCode?: string;
  readonly applicationStatus?: string;
  readonly applicationSubstatus?: string;
  readonly backOfficeAccountNumber?: string;
  readonly userSubmittedDateTime?: number;
  readonly createdDateTime?: number;
  readonly updatedDateTime?: number;
  readonly data?: SwiftIdApplicationData;
  readonly userId?: number;
}

/**
 * Generates a SwiftID application object
 */
export function generateSwiftIdApplicationObject({
  id = generateId(),
  uuid = uuidv4(),
  advisorCode = 'ABC',
  brokerageCode = 'DEF',
  branchCode = 'GHI',
  applicationStatus = 'Created',
  applicationSubstatus = undefined,
  backOfficeAccountNumber = undefined,
  userSubmittedDateTime = Date.now(),
  createdDateTime = Date.now(),
  updatedDateTime = Date.now(),
  data = undefined,
  userId = generateId(),
}: GenerateSwiftIdApplicationOptions): SwiftIdApplication {
  const appData: SwiftIdApplicationData = data || {
    accountType: 'Individual',
    applicants: [
      {
        title: 'Mr',
        givenName: 'Joshy',
        surname: 'Dorsy',
        hasNoMiddleName: true,
        dateOfBirth: '01/01/1975',
        emailAddress: 'jdoros@freelancer.com',
        residentialAddress: {
          streetAddress: 'Lovely Churches',
          cityOrSuburb: 'Lovely Banks',
          state: 'VIC',
          postCode: '3213',
          countryCode: 'AU',
        },
        contactDetails: {
          mobilePhoneCountryCode: 'AU',
          mobilePhone: '412345678',
        },
        countryOfBirth: 'AU',
        isTaxResidentOfAustralia: false,
        isCountryOfCitizenshipSameAsCountryOfBirth: true,
        isTfnExempted: false,
        isTaxResidentOfAForeignCountry: true,
        foreignTaxResidences: [
          {
            country: 'US',
            tinAvailable: true,
            taxIdentificationNumber: '123-45-6789',
          },
          {
            country: 'PH',
            tinAvailable: false,
            tinUnavailableReason: 'CountryDoesNotRequireTinDisclosure',
          },
          {
            country: 'SG',
            tinAvailable: true,
            taxIdentificationNumber: 'S9812345A',
          },
        ],
        isGovernmentAffiliated: false,
        isAsxAffiliated: false,
        occupationType: 'OccupationTypeBusinessOwners',
        occupationCategory: 'OccupationCategoryCafeAndRestaurant',
        businessName: 'My Cafe',
        businessAddress: {
          streetAddress: 'Lovely Churches',
          cityOrSuburb: 'Lovely Banks',
          state: 'VIC',
          postCode: '3213',
          countryCode: 'AU',
        },
        ownsACafeOrRestaurant: 'Cafe',
        sourceOfWealth: 'GbstInvestmentsSavings',
        isPrimary: true,
        applicationRelationships: [],
      },
    ],
    chessSponsorship: {
      newHin: true,
      transferHoldingsOption: 'None',
      holdings: [],
      currentSponsoringBroker: '',
      holderIdentificationNumber: '',
      chessAddressType: 'UsePrimaryApplicantAddress',
      convertHoldings: false,
    },
  };
  return {
    id,
    uuid,
    advisorCode,
    brokerageCode,
    branchCode,
    applicationStatus,
    applicationSubstatus,
    backOfficeAccountNumber,
    userSubmittedDateTime,
    createdDateTime,
    updatedDateTime,
    data: appData,
  };
}

/**
 * Generates a SwiftID company application object
 */
export function generateSwiftIdCompanyApplicationObject(
  options: Partial<GenerateSwiftIdApplicationOptions> = {},
): SwiftIdApplication {
  const companyData: SwiftIdApplicationData = {
    accountType: 'Company',
    applicants: [
      {
        title: 'Ms',
        givenName: 'Jane',
        surname: 'Smith',
        hasNoMiddleName: true,
        dateOfBirth: '15/06/1980',
        emailAddress: 'jane.smith@company.com',
        residentialAddress: {
          streetAddress: '123 Business Street',
          cityOrSuburb: 'Sydney',
          state: 'NSW',
          postCode: '2000',
          countryCode: 'AU',
        },
        contactDetails: {
          mobilePhoneCountryCode: 'AU',
          mobilePhone: '423456789',
        },
        countryOfBirth: 'AU',
        isTaxResidentOfAustralia: true,
        isCountryOfCitizenshipSameAsCountryOfBirth: true,
        isTfnExempted: false,
        taxFileNumber: '123456789',
        isTaxResidentOfAForeignCountry: false,
        isGovernmentAffiliated: false,
        isAsxAffiliated: false,
        occupationType: 'OccupationTypeExecutiveManager',
        occupationCategory: 'OccupationCategoryFinancialServices',
        sourceOfWealth: 'GbstEmployment',
        roleInEntity: 'Director',
        isPrimary: true,
        applicationRelationships: ['CompanyDirector', 'AuthorizedSignatory'],
      },
    ],
    companyDetails: {
      companyName: 'Tech Solutions Pty Ltd',
      companyType: 'Private',
      acn: '123456789',
      dateOfRegistration: '01/01/2020',
      industry: 'Technology',
      natureOfBusiness: 'Software Development',
      sourceOfWealth: 'InvestmentsAndDividends',
      useRegisteredOfficeAddress: true,
      abn: '12345678901',
      taxIdentificationCountry: 'AU',
      taxFileNumber: '987654321',
      isTfnAvailable: true,
      countryOfIncorporation: 'AU',
      tinAvailable: true,
      taxIdentificationNumber: '987654321',
      registeredOfficeAddress: {
        streetAddress: '456 Corporate Avenue',
        cityOrSuburb: 'Melbourne',
        state: 'VIC',
        postCode: '3000',
        countryCode: 'AU',
      },
      principalPlaceOfBusiness: {
        streetAddress: '789 Business Park Drive',
        cityOrSuburb: 'Brisbane',
        state: 'QLD',
        postCode: '4000',
        countryCode: 'AU',
      },
    },
    companyTaxInfo: {
      taxClassification: 'ActiveNonForeignEntityOrForeignCharity',
      hasGiin: false,
      fatcaStatus: 'ExemptBeneficialOwner',
      isAnyCompanyControllingIndividualOfForeignTaxResidence: false,
    },
    chessSponsorship: {
      newHin: true,
      transferHoldingsOption: 'None',
      holdings: [],
      currentSponsoringBroker: '',
      holderIdentificationNumber: '',
      chessAddressType: 'UseCompanyAddress',
      convertHoldings: false,
    },
  };

  return generateSwiftIdApplicationObject({
    advisorCode: 'ABC',
    brokerageCode: 'DEF',
    branchCode: 'GHI',
    applicationStatus: 'Submitted',
    userSubmittedDateTime: Date.now(),
    createdDateTime: Date.now(),
    updatedDateTime: Date.now(),
    ...options,
    data: companyData,
  });
}
